import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { alpha, Box, Button, Chip, Stack, Typography, useTheme } from "@mui/material";
import Carousel from "react-material-ui-carousel";
import type { CourseDetailsPageData } from "../../hook/useCourseDetailsPageData";
import { getCourseGlassSurfaceSx } from "./const/surface";
import MediaTile from "./MediaTile";

export type HeroSectionProps = Readonly<{
  data: CourseDetailsPageData;
}>;

export default function HeroSection({ data }: HeroSectionProps) {
  const theme = useTheme();

  const heroMediaHeight = { xs: 260, md: 360 } as const;
  const hasSharingItems = data.sharingItems.length > 0;
  const hasHeroMedia = data.heroMediaItems.length > 0;
  const heroDescription = data.descriptionContent.paragraphs[0] ?? data.pageMap.status.notice;

  return (
    <Stack component="section" spacing={{ xs: 4, md: 6 }} sx={{ pt: { xs: 2.5, md: 6 }, pb: { xs: 3, md: 5 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: !hasHeroMedia ? "1fr" : { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 5fr)" },
          gap: { xs: 2, md: 4 },
          alignItems: "stretch",
        }}
      >
        <Stack spacing={{ xs: 2, md: 2.5 }} sx={{ height: 1, justifyContent: "space-between", order: { xs: 2, md: 1 } }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={data.pageMap.status.label}
              variant="outlined"
              sx={{
                borderRadius: 0,
                borderColor: alpha(theme.palette.primary.contrastText, 0.22),
                color: alpha(theme.palette.primary.contrastText, 0.88),
                bgcolor: alpha(theme.palette.primary.contrastText, 0.04),
                fontFamily: theme.typography.fontFamily,
                "& .MuiChip-label": {
                  fontFamily: theme.typography.fontFamily,
                },
              }}
            />
            <Chip
              icon={<WorkspacePremiumOutlinedIcon />}
              label={data.courseName}
              variant="outlined"
              sx={{
                borderRadius: 0,
                borderColor: alpha(theme.palette.info.main, 0.28),
                color: theme.palette.primary.contrastText,
                bgcolor: alpha(theme.palette.info.main, 0.08),
                fontFamily: theme.typography.fontFamily,
                "& .MuiChip-label": {
                  fontFamily: theme.typography.fontFamily,
                },
              }}
            />
          </Stack>

          <Stack spacing={{ xs: 1.25, md: 2 }}>
            <Typography variant="h2" color="primary.contrastText" sx={{ typography: { xs: "h3", md: "h2" } }}>
              {data.courseName}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                typography: { xs: "body1", md: "h5" },
                color: alpha(theme.palette.primary.contrastText, 0.82),
                maxWidth: 680,
              }}
            >
              {data.heroSummary}
            </Typography>
            <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.72), maxWidth: 680 }}>
              {heroDescription}
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.25, sm: 2 }}>
            <Button
              variant="contained"
              endIcon={<ArrowOutwardRoundedIcon />}
              onClick={data.heroPrimaryAction.onClick}
              sx={{
                minHeight: 48,
                width: { xs: 1, sm: "auto" },
                borderRadius: 0,
                bgcolor: "info.main",
                boxShadow: 6,
                "&:hover": { bgcolor: "info.dark", boxShadow: 8 },
              }}
            >
              {data.heroPrimaryAction.label}
            </Button>
            {hasSharingItems ? (
              <Button
                variant="outlined"
                onClick={() => document.getElementById("sharing")?.scrollIntoView({ behavior: "smooth" })}
                sx={{
                  minHeight: 48,
                  width: { xs: 1, sm: "auto" },
                  borderRadius: 0,
                  borderColor: alpha(theme.palette.primary.contrastText, 0.2),
                  color: "primary.contrastText",
                  "&:hover": {
                    borderColor: alpha(theme.palette.primary.contrastText, 0.42),
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.04),
                  },
                }}
              >
                查看學員分享
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {!hasHeroMedia ? null : (
          <Stack
            spacing={1.5}
            sx={{
              width: 1,
              height: heroMediaHeight,
              maxWidth: { xs: 1, md: 420 },
              justifySelf: { lg: "end" },
              order: { xs: 1, md: 2 },
            }}
          >
            <HeroMediaCarousel data={data} height={heroMediaHeight} />
          </Stack>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: { xs: 1.25, md: 2 } }}>
        {data.keyFacts.map((item) => (
          <Stack
            key={item.key}
            spacing={0.75}
            sx={{
              p: { xs: 2, md: 2.5 },
              ...getCourseGlassSurfaceSx(theme),
            }}
          >
            <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.contrastText, 0.64) }}>
              {item.label}
            </Typography>
            <Typography variant="h6" color="primary.contrastText">
              {item.value}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Stack>
  );
}

function HeroMediaCarousel({ data, height }: Readonly<{ data: CourseDetailsPageData; height: { xs: number; md: number } }>) {
  if (!data.heroMediaItems.length) return null;

  const shouldAutoPlay = data.playingVideoId == null;

  return (
    <Box
      sx={{
        height,
        overflow: "hidden",
      }}
    >
      <Carousel
        indicators={false}
        autoPlay={shouldAutoPlay}
        stopAutoPlayOnHover={true}
        duration={1500}
        fullHeightHover={true}
        navButtonsAlwaysVisible={true}
        height="100%"
        sx={{ height: 1 }}
      >
        {data.heroMediaItems.map((media) => (
          <MediaTile
            key={media.id}
            title={media.title}
            thumbnailImage={media.thumbnailImage}
            isVideo={media.kind === "video"}
            embedUrl={media.kind === "video" ? media.embedUrl : undefined}
            isPlaying={media.kind === "video" ? data.playingVideoId === media.id : false}
            onPlayToggle={media.kind === "video" ? () => data.togglePlayingVideo(media.id) : undefined}
            actionLabel={media.actionLabel}
            onAction={media.kind === "video" ? () => data.togglePlayingVideo(media.id) : media.onAction}
            fillHeight
            rootSx={{
              p: 0,
              border: 0,
              background: "transparent",
              backdropFilter: "none",
              boxShadow: "none",
              height: 1,
            }}
            mediaSx={{ border: 0 }}
          />
        ))}
      </Carousel>
    </Box>
  );
}
